import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { PaymentMethod } from '@jwp/ott-common/types/checkout';
import CreditCard from '@jwp/ott-theme/assets/icons/creditcard.svg?react';
import PayPal from '@jwp/ott-theme/assets/icons/paypal.svg?react';
import IdealWero from '@jwp/ott-theme/assets/icons/ideal_wero.svg?react';
import classNames from 'classnames';

import Button from '../Button/Button';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';
import Icon from '../Icon/Icon';
import { useAriaAnnouncer } from '../../containers/AnnouncementProvider/AnnoucementProvider';

import styles from './PaymentMethodForm.module.scss';

type Props = {
  paymentMethodId?: number;
  onCloseButtonClick: () => void;
  paymentMethods?: PaymentMethod[];
  onPaymentMethodChange: React.ChangeEventHandler<HTMLInputElement>;
  renderPaymentMethod?: () => React.JSX.Element | null;
  submitting: boolean;
  updateSuccess: boolean;
  allowChangingPaymentMethods: boolean;
};

const PaymentMethodForm: React.FC<Props> = ({
  paymentMethodId,
  paymentMethods,
  onCloseButtonClick,
  onPaymentMethodChange,
  renderPaymentMethod,
  submitting,
  updateSuccess,
  allowChangingPaymentMethods,
}) => {
  const { t } = useTranslation('account');
  const announce = useAriaAnnouncer();
  // t('payment.longer_than_usual');

  const cardPaymentMethod = paymentMethods?.find((method) => method.methodName === 'card');
  const idealUpdateSupported = false; // @todo: ideal payment update requires a purchase. flow update needed
  const idealPaymentMethod = idealUpdateSupported ? paymentMethods?.find((method) => method.methodName === 'ideal') : undefined;
  const paypalPaymentMethod = paymentMethods?.find((method) => method.methodName === 'paypal');

  useEffect(() => {
    if (updateSuccess) {
      announce(t('checkout.payment_success'), 'success');
    }
  }, [updateSuccess, announce, t]);

  return (
    <>
      <h1 className={styles.title}>{t('payment.update_payment_details')}</h1>
      {updateSuccess ? (
        <>
          <p className={styles.success}>{t('payment.update_payment_success')}</p>
          <Button label={t('payment.back_to_profile')} onClick={onCloseButtonClick} color="primary" fullWidth />
        </>
      ) : (
        <>
          {/* temporary flag to hide payment methods when the payment provider provides this (Adyen Dropin) */}
          {allowChangingPaymentMethods ? (
            <div className={styles.paymentMethodsInputs}>
              {cardPaymentMethod ? (
                <div className={styles.paymentMethod}>
                  <input
                    className={styles.radio}
                    type="radio"
                    name="paymentMethod"
                    value={cardPaymentMethod.id}
                    id="card"
                    checked={paymentMethodId === cardPaymentMethod.id}
                    onChange={onPaymentMethodChange}
                  />
                  <label className={styles.paymentMethodLabel} htmlFor="card">
                    <Icon icon={CreditCard} />
                    {t('payment.credit_card')}
                  </label>
                </div>
              ) : null}
              {paypalPaymentMethod ? (
                <div className={styles.paymentMethod}>
                  <input
                    className={styles.radio}
                    type="radio"
                    name="paymentMethod"
                    value={paypalPaymentMethod.id}
                    id="paypal"
                    checked={paymentMethodId === paypalPaymentMethod.id}
                    onChange={onPaymentMethodChange}
                  />
                  <label className={styles.paymentMethodLabel} htmlFor="paypal">
                    <Icon icon={PayPal} /> {t('payment.paypal')}
                  </label>
                </div>
              ) : null}
              {idealPaymentMethod ? (
                <div className={classNames(styles.paymentMethod, styles.idealWero)}>
                  <input
                    className={styles.radio}
                    type="radio"
                    name="paymentMethod"
                    value={idealPaymentMethod.id}
                    id="ideal"
                    checked={paymentMethodId === idealPaymentMethod.id}
                    onChange={onPaymentMethodChange}
                  />
                  <label className={styles.paymentMethodLabel} htmlFor="ideal">
                    <span className={styles.srOnly}>{t('payment.wero')}</span>
                    <Icon icon={IdealWero} />
                  </label>
                </div>
              ) : null}
            </div>
          ) : null}
          <div>{renderPaymentMethod ? renderPaymentMethod() : null}</div>
          {submitting && <LoadingOverlay transparentBackground inline />}
        </>
      )}
    </>
  );
};

export default PaymentMethodForm;
